/**
 * Trainer Store
 *
 * Zustand (vanilla) store for the trainer's roster. `GET /api/trainer/clients`
 * returns accepted clients and pending requests in one list, partitioned on
 * `status`, so the store holds a single array and the page slices it.
 *
 * The roster is fetched unpaginated (`paginate: 'false'`) and then filtered,
 * sorted and searched against the held list — the endpoint's own `sort`
 * whitelist cannot express "biggest drop", and a triage list that re-requests
 * on every chip click is a page that flickers while a trainer is reading it.
 *
 * Accept/decline patch the one row that changed rather than refetching. The
 * endpoint returns no body, so the store applies what the server would: an
 * accepted request becomes an `Accepted` row in place, a declined one is
 * dropped. `getClients` is a raw `apiClient` call rather than a `CrudService`
 * read, so there is no response cache behind it to go stale — the patch saves a
 * round trip, it is not working around one.
 */

import { createStore } from 'zustand/vanilla';

import { failureMessage } from './failure';
import { registerSessionScoped } from './session-scope';
import { trainerService } from '../services/trainer.service';

import type {
  TrainerClient,
  TrainerClientQuery,
  TrainerContractTerms,
  TrainerRequestAction,
} from '../types/domain/trainer.types';

// =============================================================================
// STORE INTERFACES
// =============================================================================

export interface TrainerStoreState {
  /** The full roster — accepted clients and pending requests together */
  clients: TrainerClient[];
  isFetching: boolean;
  fetchError: string | null;
  /**
   * Username of the request currently being answered, so the strip can disable
   * one pair of buttons rather than the whole panel.
   */
  respondingTo: string | null;
  respondError: string | null;
}

export interface TrainerStoreActions {
  fetchClients: (query?: TrainerClientQuery) => Promise<void>;
  /**
   * Accept or decline a pending request, patching the held list in place.
   * Resolves `false` on failure — the caller renders `respondError` rather than
   * catching, so this does not re-throw.
   */
  /** Accepting needs the contract terms; declining is the action alone. */
  respondToRequest: (
    username: string,
    action: TrainerRequestAction,
    terms?: TrainerContractTerms
  ) => Promise<boolean>;
  clearErrors: () => void;
  reset: () => void;
}

export type TrainerStore = TrainerStoreState & TrainerStoreActions;

// =============================================================================
// STORE IMPLEMENTATION
// =============================================================================

const INITIAL_STATE: TrainerStoreState = {
  clients: [],
  isFetching: false,
  fetchError: null,
  respondingTo: null,
  respondError: null,
};

export const trainerStore = createStore<TrainerStore>((set, get) => ({
  ...INITIAL_STATE,

  fetchClients: async (query?: TrainerClientQuery): Promise<void> => {
    set({ isFetching: true, fetchError: null });

    try {
      const clients = await trainerService.getClients(query);
      set({ clients });
    } catch (error) {
      set({ fetchError: failureMessage(error, 'Failed to fetch clients') });
    } finally {
      set({ isFetching: false });
    }
  },

  respondToRequest: async (
    username: string,
    action: TrainerRequestAction,
    terms?: TrainerContractTerms
  ): Promise<boolean> => {
    set({ respondingTo: username, respondError: null });

    try {
      await trainerService.respondToClientRequest(username, action, terms);

      set({
        clients:
          action === 'accept'
            ? get().clients.map((client) =>
                client.username === username
                  ? { ...client, status: 'Accepted' }
                  : client
              )
            : // A declined request leaves no row: the pivot is deleted, so the
              // client is no longer on the roster in any state.
              get().clients.filter((client) => client.username !== username),
      });
      return true;
    } catch (error) {
      set({
        respondError: failureMessage(
          error,
          `Failed to ${action} the request from @${username}`
        ),
      });
      return false;
    } finally {
      set({ respondingTo: null });
    }
  },

  clearErrors: (): void => {
    set({ fetchError: null, respondError: null });
  },

  reset: (): void => {
    set({ ...INITIAL_STATE });
  },
}));

// =============================================================================
// SELECTORS
// =============================================================================

export const selectClients = (state: TrainerStore): TrainerClient[] =>
  state.clients;

// =============================================================================
// SESSION SCOPE
// =============================================================================

registerSessionScoped('trainerStore', () => {
  trainerStore.getState().reset();
});
